import {
  Outlet,
  createRootRoute,
  redirect,
  useRouter,
  useRouterState
} from '@tanstack/react-router'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX
} from 'react'
import { Toaster, toast } from 'sonner'

import {
  AlertMe,
  PageAlertProvider,
  usePageAlert
} from '../components/alert-me.tsx'
import {
  DisplaySurface,
  useDisplaying,
  useDisplayErrorRecovery
} from '../components/display-runtime.tsx'
import { PageStar } from '../components/page-star.tsx'
import { PagePalette } from '../components/page-palette.tsx'
import { useTheme } from '../components/theme-provider.tsx'
import { useFigureFocus } from '../hooks/use-figure-focus.ts'
import { SavedViewAlertHost } from '../components/saved-view-alert.tsx'
import { SaveViewOfferProvider } from '../components/save-view-offer.tsx'
import { LandingNavigationContext } from '../components/landing-navigation.tsx'
import {
  AppNav,
  SIDEBAR_CONTROL_CLASS,
  SidebarFooter
} from '../components/console-rail.tsx'
import { Button } from '../design-system/button.tsx'
import { EmptyState } from '../design-system/empty-state.tsx'
import { ButtonLink } from '../design-system/link.tsx'
import { Panel } from '../design-system/panel.tsx'
import { Icon } from '../design-system/icon.tsx'
import { NavSheet } from '../design-system/nav-sheet.tsx'
import { Notice } from '../design-system/notice.tsx'
import {
  usePreferences,
  usePreferencesConnected
} from '../hooks/use-preferences.ts'
import { useViewerAccess } from '../hooks/use-viewer-access.ts'
import {
  accessibleNavSections,
  pageForPath,
  moveNavSection,
  orderNavSections,
  pagesInStoredOrder,
  withRecentPage,
  toggleStarred,
  STARRED_PAGE_LIMIT,
  footPages,
  pinnedPages,
  railPages,
  groupInternalPages
} from '../lib/pages.ts'
import { movedPageLocation } from '../lib/moved-pages.ts'
import {
  buildPaletteActions,
  type PaletteActionId
} from '../lib/palette-action.ts'
import { INTERNAL_PAGES } from './-nav.ts'
import { resetDisplaySession } from '../lib/display-mode.ts'
import { IDLE_WARNING_EVENT } from '../lib/idle.ts'
import {
  getPreferences,
  updatePreferences,
  type PreferencePatch
} from '../lib/preference-store.ts'
import { useSession } from '../session.tsx'
import { useDisplayPreview } from '../components/display-preview-context.tsx'

/**
 * The stop a viewer sees for a page the rail would not open.
 *
 * The same notice is used in the console and on a display, so a denied
 * playlist page reads the same way.
 *
 * @param props - Which stop to show.
 * @param props.unavailable - The check failed, rather than the viewer being refused.
 * @returns The notice.
 */
function PageAccessStop({
  unavailable
}: {
  unavailable: boolean
}): JSX.Element {
  if (unavailable) {
    return (
      <Notice data-testid="page-access-unavailable" tone="warning">
        Page access is temporarily unavailable. Reload this page to try again.
      </Notice>
    )
  }

  return (
    <Panel data-testid="page-access-denied" title="Page unavailable">
      <EmptyState
        data-testid="page-access-denied-message"
        title="You do not have access to this page"
        description="Ask the page owner for access to a listed group."
      />
    </Panel>
  )
}

/**
 * What an address with no page shows, inside the console so the rail and
 * the menu still offer every page. The router's own default is a bare line
 * of text at the top left.
 *
 * @returns The panel, with the way back to the landing page.
 */
function PageNotFound(): JSX.Element {
  const router = useRouter()
  return (
    <Panel data-testid="not-found" title="Page not found">
      <EmptyState
        data-testid="not-found-empty"
        title="Nothing lives at this address"
        description="Check the address, or pick a page from the menu."
        action={
          <ButtonLink
            data-testid="not-found-home"
            href="/"
            variant="outline"
            onNavigate={() => void router.navigate({ to: '/' })}
          >
            Go to home
          </ButtonLink>
        }
      />
    </Panel>
  )
}

export const Route = createRootRoute({
  beforeLoad: ({ location }) => {
    const next = movedPageLocation(
      location.pathname,
      location.searchStr,
      location.hash
    )
    if (next !== undefined) {
      throw redirect({ href: next, replace: true })
    }
  },
  component: RootRoute,
  notFoundComponent: PageNotFound,
  errorComponent: function RouterError({ error }) {
    useDisplayErrorRecovery(useRouter())
    return (
      <div className="p-8">
        <Notice tone="danger" data-testid="router-error">
          {error.message}
        </Notice>
      </div>
    )
  }
})

/**
 * Every declared section, grouped once.
 *
 * `INTERNAL_PAGES` is frozen, so the grouping cannot change while the app
 * runs. `groupInternalPages` also returns the pages carrying no group; every
 * page here carries one and `-nav.test.ts` keeps it that way, so the rail
 * reads the sections alone. The framework keeps its flat branch for a rail
 * that may not be this one.
 *
 * A page with `inRail: false` opens only from a link and is left out here. A
 * pinned page is left out too: the rail's foot shows it instead.
 *
 * The rail shows these only after the backend's page access filters them.
 */
const NAV_SECTIONS = groupInternalPages(railPages(INTERNAL_PAGES)).sections
const MERCHANT_360_PATH =
  INTERNAL_PAGES.find(page => page.id === 'merchant-360')?.path ?? null

/** The pages the rail pins above the account row, before page access filters them. */
const PINNED_PAGES = pinnedPages(INTERNAL_PAGES)

/** An empty list, shared so a reader with no choice keeps one identity across renders. */
const NO_NAMES: readonly string[] = Object.freeze([])

/** The width where the desktop rail replaces the mobile sheet. */
const DESKTOP_RAIL_BREAKPOINT = 1024

/** What the rail says when a group or page change could not be held. */
const RAIL_UNSAVED_MESSAGE =
  'This change to the rail is not saved. It lasts until you reload.'

/**
 * The category panel starts closed.
 *
 * Opening a report changes the path, and the panel closes in that render.
 * A click on the report that is already open changes no path, so the rail
 * also closes the panel in its own navigate handler.
 */
function useCategoryPanel(pathname: string): {
  openGroup: string | null
  toggleGroup: (group: string) => void
  closeGroup: () => void
} {
  const [trackedPath, setTrackedPath] = useState(pathname)
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const closeGroup = useCallback(() => setOpenGroup(null), [])
  if (trackedPath !== pathname) {
    setTrackedPath(pathname)
    setOpenGroup(null)
  }

  return {
    openGroup,
    toggleGroup: group => setOpenGroup(open => (open === group ? null : group)),
    closeGroup
  }
}

/** The console, with the one alert sheet search and the button both open. */
function RootRoute(): JSX.Element {
  const preview = useDisplayPreview()
  const router = useRouter()
  const displaying = useDisplaying(router)
  if (preview)
    return (
      <PageAlertProvider enabled={false}>
        <PreviewLayout />
      </PageAlertProvider>
    )
  return (
    <PageAlertProvider enabled={!displaying}>
      <RootLayout />
    </PageAlertProvider>
  )
}

/** Apply the normal employee access check without mounting console or display effects. */
function PreviewLayout(): JSX.Element {
  const { userName } = useSession()
  const access = useViewerAccess(userName)
  const pathname = useRouterState({ select: state => state.location.pathname })
  const page = pageForPath(INTERNAL_PAGES, pathname)
  if (access.data === undefined)
    return access.isError ? (
      <PageAccessStop unavailable />
    ) : (
      <Notice data-testid="page-access-loading" tone="info">
        Checking page access…
      </Notice>
    )
  if (
    !page ||
    pathname.startsWith('/platform/displays') ||
    !access.data.pageIds.includes(page.id)
  ) {
    return (
      <PageAccessStop
        unavailable={
          page !== undefined && access.data.unavailablePageIds.includes(page.id)
        }
      />
    )
  }
  return <Outlet />
}

/** The shell follows super's desktop rail and mobile navigation sheet. */
function RootLayout(): JSX.Element {
  useFigureFocus()
  const router = useRouter()
  const { userName, error, onSignOut } = useSession()
  const { theme, toggle } = useTheme()
  const pageAlert = usePageAlert()
  const access = useViewerAccess(userName)
  const allowedIds = access.data?.pageIds ?? NO_NAMES
  // These lists are new arrays on every call. Memoising them here keeps the
  // landing context stable while access and the saved lists do not change.
  const availableSections = useMemo(
    () => accessibleNavSections(NAV_SECTIONS, allowedIds),
    [allowedIds]
  )
  const foot = useMemo(
    () => footPages(PINNED_PAGES, access.data),
    [access.data]
  )
  const pathname = useRouterState({ select: state => state.location.pathname })
  const displaying = useDisplaying(router)
  const searchStr = useRouterState({
    select: state => state.location.searchStr
  })
  const currentPage = pageForPath(INTERNAL_PAGES, pathname)
  const currentPageDenied =
    currentPage !== undefined &&
    access.data !== undefined &&
    !access.data.pageIds.includes(currentPage.id)
  const [menuOpen, setMenuOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  // Which account menu is open. Search's time-zone command opens the same
  // menu the account row opens, and the two feet are not on screen together.
  const [accountMenu, setAccountMenu] = useState<'desktop' | 'mobile' | null>(
    null
  )
  const onMenuOpenChange = useCallback(
    (open: boolean) => {
      setMenuOpen(open)
      if (!open) setAccountMenu(null)
    },
    [setMenuOpen, setAccountMenu]
  )
  const onPaletteOpenChange = useCallback(
    (open: boolean) => {
      setPaletteOpen(open)
      if (open) onMenuOpenChange(false)
    },
    [onMenuOpenChange]
  )
  // Saved section order applies to both rails (ADR-0014).
  const preferences = usePreferences()
  const preferencesConnected = usePreferencesConnected()
  const starredIds = preferences.starredPages ?? NO_NAMES
  const recentIds = preferences.recentPages ?? NO_NAMES
  const sectionOrder = preferences.sectionOrder ?? NO_NAMES
  // Recent and Tools collapse on the reader's settings, so the choice follows
  // them to any machine. Absent, both are open.
  const collapsedRailLists = preferences.collapsedRailLists ?? NO_NAMES
  const recentCollapsed = collapsedRailLists.includes('recent')
  const toolsCollapsed = collapsedRailLists.includes('tools')
  const starredPages = useMemo(
    () => pagesInStoredOrder(INTERNAL_PAGES, starredIds, allowedIds),
    [starredIds, allowedIds]
  )
  const recentPages = useMemo(
    () => pagesInStoredOrder(INTERNAL_PAGES, recentIds, allowedIds),
    [recentIds, allowedIds]
  )
  const { openGroup, toggleGroup, closeGroup } = useCategoryPanel(pathname)
  const sections = useMemo(
    () => orderNavSections(availableSections, sectionOrder),
    [availableSections, sectionOrder]
  )
  // Both rails show the same mode.
  const [customizing, setCustomizing] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const menuTrigger = useRef<HTMLButtonElement>(null)
  const content = useRef<HTMLElement>(null)

  useEffect(() => {
    const close = (): void => onMenuOpenChange(false)
    window.addEventListener(IDLE_WARNING_EVENT, close)
    return () => window.removeEventListener(IDLE_WARNING_EVENT, close)
  }, [onMenuOpenChange])

  function closeMenu(): void {
    onMenuOpenChange(false)
  }

  // A change that is not held still applies to the rail. The reader is told,
  // and the choice simply does not outlive the tab.
  function saveRail(patch: PreferencePatch): void {
    void updatePreferences(patch).then(result => {
      setSaveError(result === 'saved' ? null : RAIL_UNSAVED_MESSAGE)
    })
  }

  function toggleRailList(list: 'recent' | 'tools'): void {
    const held = getPreferences().collapsedRailLists ?? NO_NAMES
    saveRail({
      collapsedRailLists: held.includes(list)
        ? held.filter(name => name !== list)
        : [...held, list]
    })
  }

  // Record the page the reader opened. Home is not a declared page. A page
  // with `inRail: false` needs an id the rail cannot supply, so it stays out
  // of Recent. A page already at the front is not written again.
  // A visit waits for the server's settings, or it would replace the saved
  // list. The reader did not make this change, so a failed save says nothing.
  useEffect(() => {
    if (!preferencesConnected) return
    if (access.data === undefined || currentPage === undefined) return
    if (currentPage.inRail === false) return
    if (!access.data.pageIds.includes(currentPage.id)) return
    const held = getPreferences().recentPages ?? NO_NAMES
    if (held[0] === currentPage.id) return
    void updatePreferences({
      recentPages: withRecentPage(held, currentPage.id)
    })
  }, [access.data, currentPage, preferencesConnected])

  const navProps = {
    pathname,
    onSelect: closeMenu,
    sections,
    openGroup,
    onToggleGroup: toggleGroup,
    onCloseGroup: closeGroup,
    starredPages,
    recentPages,
    onMoveSection: (group: string, direction: -1 | 1) => {
      const savedOrder = getPreferences().sectionOrder ?? NO_NAMES
      const current = orderNavSections(availableSections, savedOrder)
      saveRail({
        sectionOrder: moveNavSection(current, group, direction, savedOrder)
      })
    },
    customizing,
    onCustomizingChange: (next: boolean) => {
      setCustomizing(next)
      if (next) closeGroup()
    },
    saveError,
    onOpenSearch: () => onPaletteOpenChange(true),
    starsEnabled: preferencesConnected,
    onToggleStar: (pageId: string) => toggleStar(pageId),
    sheetOpen: menuOpen,
    recentCollapsed,
    onToggleRecent: () => toggleRailList('recent'),
    accessStatus:
      access.data !== undefined
        ? ('ready' as const)
        : access.isError
          ? ('error' as const)
          : ('loading' as const)
  }

  const openCustomization = useCallback(() => {
    setCustomizing(true)
    if (window.innerWidth < DESKTOP_RAIL_BREAKPOINT) setMenuOpen(true)
  }, [setMenuOpen])
  const merchant360Path =
    access.data?.pageIds.includes('merchant-360') === true
      ? MERCHANT_360_PATH
      : null
  // Home and the palette share this list, including pinned tools.
  // A link-only page is in neither list, so search does not offer it.
  const landingNavigation = useMemo(
    () => ({
      sections,
      pages: [
        ...sections.flatMap(section =>
          section.pages.map(page => ({ page, group: section.group }))
        ),
        ...foot.map(page => ({ page, group: page.group ?? 'Pinned' }))
      ],
      starredPages,
      recentPages,
      merchant360Path,
      openCustomization
    }),
    [
      sections,
      foot,
      starredPages,
      recentPages,
      merchant360Path,
      openCustomization
    ]
  )
  // The zone list is the account menu's select. One command cannot name one
  // of those zones, so search opens that menu on the foot that is on screen.
  const openTimeZone = (): void => {
    if (window.innerWidth < DESKTOP_RAIL_BREAKPOINT) {
      setMenuOpen(true)
      setAccountMenu('mobile')
      return
    }
    setAccountMenu('desktop')
  }
  // The star and Alert me render only on a page the reader can see. Search
  // offers them on that same page, and not while access or the settings are
  // still arriving.
  const pageChrome =
    access.data !== undefined &&
    !currentPageDenied &&
    !(currentPage === undefined && pathname !== '/')
  // The star waits for the server's settings, or a press would replace the
  // starred list saved on another machine.
  const showStar =
    pageChrome &&
    preferencesConnected &&
    currentPage !== undefined &&
    currentPage.inRail !== false
  function toggleStar(pageId: string): void {
    const starred = getPreferences().starredPages ?? NO_NAMES
    const next = toggleStarred(
      starred,
      pageId,
      starredPages.map(page => page.id)
    )
    if (next === starred) {
      toast.error(
        `Starred holds ${STARRED_PAGE_LIMIT} pages. Unstar one to add this page.`
      )
      return
    }
    saveRail({ starredPages: next })
  }
  function togglePageStar(): void {
    if (currentPage === undefined) return
    toggleStar(currentPage.id)
  }
  const paletteActions = buildPaletteActions({
    star:
      showStar && currentPage !== undefined
        ? {
            starred: (preferences.starredPages ?? NO_NAMES).includes(
              currentPage.id
            )
          }
        : null,
    alert: pageChrome && pageAlert.report !== null,
    theme
  })
  function onPaletteAction(id: PaletteActionId): void {
    switch (id) {
      case 'star':
        togglePageStar()
        break
      case 'alert':
        pageAlert.openAlert()
        break
      case 'time-zone':
        openTimeZone()
        break
      case 'theme':
        toggle()
        break
      case 'customize':
        openCustomization()
        break
      case 'sign-out':
        onSignOut()
        break
    }
  }
  const confirmedPerson = access.data?.displayAccount === false
  const leaveDisplay = useCallback(() => {
    resetDisplaySession()
    void router.navigate({ to: '/' })
  }, [router])
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (
        !displaying ||
        !confirmedPerson ||
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        (event.target instanceof Element &&
          event.target.closest(
            '[role="dialog"], [role="menu"], [role="listbox"]'
          ))
      )
        return
      leaveDisplay()
    }
    if (displaying && confirmedPerson) {
      window.addEventListener('keydown', onKey)
    }
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [displaying, confirmedPerson, leaveDisplay])

  if (displaying) {
    return (
      <div
        data-testid="app-root"
        className={`bg-background text-foreground min-h-svh${confirmedPerson ? ' relative' : ' cursor-none'}`}
      >
        {confirmedPerson ? (
          <Button
            data-testid="display-exit"
            variant="quiet"
            size="sm"
            className="absolute end-3 top-3 z-10"
            onClick={leaveDisplay}
          >
            Exit display
          </Button>
        ) : null}
        <LandingNavigationContext.Provider value={landingNavigation}>
          <DisplaySurface
            pages={INTERNAL_PAGES}
            accessState={
              access.data !== undefined
                ? 'ready'
                : access.isError
                  ? 'error'
                  : 'loading'
            }
            replacement={
              currentPageDenied &&
              currentPage !== undefined &&
              access.data !== undefined ? (
                <PageAccessStop
                  unavailable={access.data.unavailablePageIds.includes(
                    currentPage.id
                  )}
                />
              ) : null
            }
          />
        </LandingNavigationContext.Provider>
      </div>
    )
  }

  return (
    <div
      data-testid="app-root"
      className="bg-background text-foreground min-h-svh lg:grid lg:grid-cols-rail print:block!"
    >
      <aside
        data-testid="console-sidebar"
        className="bg-sidebar relative z-20 hidden lg:block print:hidden!"
      >
        {/* The aside stretches to the grid row, which is as tall as the report
            beside it. The foot pins to the viewport, not to that row, so the
            height and the stick belong on this inner box. */}
        <div className="sticky top-0 flex h-svh flex-col">
          <AppNav {...navProps} />
          <SidebarFooter
            onSelect={closeMenu}
            pathname={pathname}
            pages={foot}
            collapsed={toolsCollapsed}
            onToggleCollapsed={() => toggleRailList('tools')}
            accountOpen={accountMenu === 'desktop'}
            onAccountOpenChange={open =>
              setAccountMenu(open ? 'desktop' : null)
            }
          />
        </div>
      </aside>
      <div className="flex min-w-0 flex-col">
        <header
          data-testid="console-topbar"
          className="bg-sidebar sticky top-0 z-10 flex items-center gap-3 px-3 py-2 lg:hidden print:hidden"
        >
          <Button
            data-testid="console-nav-toggle"
            ref={menuTrigger}
            variant="quiet"
            size="icon"
            aria-label="Menu"
            aria-expanded={menuOpen}
            className={SIDEBAR_CONTROL_CLASS}
            onClick={() => setMenuOpen(true)}
          >
            <Icon name="menu" />
          </Button>
          <span className="text-sidebar-foreground truncate text-sm">
            {userName}
          </span>
        </header>
        <NavSheet
          data-testid="console-nav"
          open={menuOpen}
          onOpenChange={onMenuOpenChange}
          title="Internal navigation"
          // The sheet is a flex column with a gap of its own, which would show
          // as a seam above the pinned foot.
          className="gap-0"
          onCloseAutoFocus={event => {
            event.preventDefault()
            const target = menuTrigger.current?.getClientRects().length
              ? menuTrigger.current
              : content.current
            target?.focus()
          }}
        >
          <AppNav {...navProps} mobile />
          <SidebarFooter
            mobile
            onSelect={closeMenu}
            pathname={pathname}
            pages={foot}
            collapsed={toolsCollapsed}
            onToggleCollapsed={() => toggleRailList('tools')}
            accountOpen={accountMenu === 'mobile'}
            onAccountOpenChange={open => setAccountMenu(open ? 'mobile' : null)}
          />
        </NavSheet>
        <main
          ref={content}
          tabIndex={-1}
          data-testid="console-content"
          // Its own stacking layer, so a table's sticky header cells (up to
          // z-40) scroll under the mobile top bar rather than over it.
          className="isolate flex min-w-0 flex-col gap-6 p-4 sm:p-6 md:p-8 print:p-0!"
        >
          {error ? (
            <Notice tone="danger" data-testid="session-error">
              {error}
            </Notice>
          ) : null}
          {currentPage === undefined && pathname !== '/' ? (
            <Outlet />
          ) : access.data === undefined ? (
            access.isError ? (
              <Notice data-testid="page-access-error" tone="danger">
                Page access could not be checked. Reload this page to try again.
              </Notice>
            ) : (
              <Notice data-testid="page-access-loading" tone="info">
                Checking page access…
              </Notice>
            )
          ) : currentPageDenied && currentPage !== undefined ? (
            <PageAccessStop
              unavailable={access.data.unavailablePageIds.includes(
                currentPage.id
              )}
            />
          ) : (
            <>
              {access.isError ? (
                <Notice data-testid="page-access-warning" tone="warning">
                  Page access could not be refreshed. Your last access check is
                  still in use.
                </Notice>
              ) : access.data.reason ? (
                <Notice data-testid="page-access-warning" tone="warning">
                  Some pages are unavailable because group membership could not
                  be checked.
                </Notice>
              ) : null}
              <LandingNavigationContext.Provider value={landingNavigation}>
                <SaveViewOfferProvider>
                  <SavedViewAlertHost>
                    <div className="flex items-center justify-end gap-2 empty:hidden">
                      {showStar && currentPage !== undefined ? (
                        <PageStar
                          title={currentPage.title}
                          pressed={(
                            preferences.starredPages ?? NO_NAMES
                          ).includes(currentPage.id)}
                          onToggle={togglePageStar}
                        />
                      ) : null}
                      <AlertMe />
                    </div>
                    <Outlet />
                  </SavedViewAlertHost>
                </SaveViewOfferProvider>
              </LandingNavigationContext.Provider>
            </>
          )}
        </main>
        <Toaster />
      </div>
      <PagePalette
        open={paletteOpen}
        onOpenChange={onPaletteOpenChange}
        pages={landingNavigation.pages}
        enabled={access.data !== undefined}
        merchant360Path={landingNavigation.merchant360Path}
        // Merchant 360 already offers its own action, and a denied page
        // shows no report to filter.
        report={
          currentPage?.takesMerchants === true &&
          currentPage.id !== 'merchant-360' &&
          !currentPageDenied
            ? { path: pathname, search: searchStr }
            : null
        }
        actions={paletteActions}
        onAction={onPaletteAction}
        starredPages={starredPages}
        recentPages={recentPages}
        onNavigate={path => {
          router.history.push(path)
        }}
      />
    </div>
  )
}
